import type { IOrganizationJSON, IRoleJSON, IMembershipInviteJSON, IProjectGroupJSON, IRegisteredThemeJSON, IRoleAssignmentJSON } from 'external-api-types';
import type { OrganizationTeam, OrganizationUser } from '../../modules/organization/Organization';
import { deriveUserTeamRelations } from '../../modules/organization/Organization';
import type { RoleAssignmentMap } from '../../modules/organization/RoleAssignment';
import { deriveRolesAssigned } from '../../modules/organization/RoleAssignment';
import type { OrganizationActions } from '../../actions/org';
import { LOGOUT_ACTION } from '../consts';
import { HYDRATE_STATE } from '../../store/consts';
import type { OrganizationPermission } from '../../types/Permission';
import type { RootState } from '../../store/app';
import organizationUsersReducer from './users';
import type { IOrganizationUsers } from './types';

export interface State {
    error?: { id: string; title: string };
    isLoading?: boolean;
    organization?: IOrganizationJSON;
    organizationId?: string;
    selectedTeamId?: string;
    membershipInvites: IMembershipInviteJSON[];
    teamById: Record<string, OrganizationTeam>;
    userById: Record<string, OrganizationUser>;
    roleByUserOrTeamId: RoleAssignmentMap;
    projectGroups: IProjectGroupJSON[];
    projectRoles: IRoleJSON[];
    roles: IRoleJSON[];
    permissions: OrganizationPermission[];
    registeredThemes: IRegisteredThemeJSON[];
    accessControlList: IRoleAssignmentJSON[];
    projectGroupCounts: { [key: string]: number };
    createdAt?: Date;
    updatedAt?: Date;
    name?: string;
    isAdmin?: boolean;
    featureFlags?: IOrganizationJSON['featureFlags'];
    allowedFeatures?: IOrganizationJSON['allowedFeatures'];
    emailDomains?: IOrganizationJSON['emailDomains'];
    id?: IOrganizationJSON['id'];
    users: IOrganizationUsers;
}

const getDefaultState = (): State => ({
    organizationId: undefined,
    isLoading: false,
    membershipInvites: [] as IMembershipInviteJSON[],
    accessControlList: [] as IRoleAssignmentJSON[],
    organization: {} as IOrganizationJSON,
    projectGroups: [] as IProjectGroupJSON[],
    registeredThemes: [] as IRegisteredThemeJSON[],
    teamById: {} as Record<string, OrganizationTeam>,
    userById: {} as Record<string, OrganizationUser>,
    projectRoles: [],
    roles: [],
    permissions: [],
    roleByUserOrTeamId: {} as RoleAssignmentMap,
    projectGroupCounts: {} as { [key: string]: number },
    error: undefined,
    selectedTeamId: '',
    users: organizationUsersReducer(undefined, null)
});

const organizationReducer = (state = getDefaultState(), action: OrganizationActions | null): State => {
    if (!action) return { ...state };
    switch (action.type) {
        case 'SET_SELECTED_TEAM_ID': {
            return {
                ...state,
                selectedTeamId: (action.data as { selectedTeamId?: string | undefined }).selectedTeamId ?? ''
            };
        }
        case 'SET_NEW_ORGANIZATION_ID': {
            return {
                ...getDefaultState(),
                organizationId: (action.data as { organizationId?: string | undefined }).organizationId
            };
        }
        case 'ORGANIZATION_FETCH_STARTED': {
            return {
                ...state,
                isLoading: true
            };
        }
        case 'ORGANIZATION_FETCH_SUCCEEDED':
        case 'ORGANIZATION_REMOVE_USER_SUCCEEDED':
        case 'ORGANIZATION_CREATE_TEAM_SUCCEEDED':
        case 'ORGANIZATION_EDIT_TEAM_SUCCEEDED':
        case 'ORGANIZATION_DELETE_TEAM_SUCCEEDED':
        case 'ORGANIZATION_CREATE_PROJECT_GROUP_SUCCEEDED':
        case 'ORGANIZATION_EDIT_PROJECT_GROUP_SUCCEEDED':
        case 'ORGANIZATION_DELETE_PROJECT_GROUP_SUCCEEDED':
        case 'ORGANIZATION_TEAM_ADD_USERS_SUCCEEDED':
        case 'ORGANIZATION_TEAM_REMOVE_USER_SUCCEEDED':
        case 'ORGANIZATION_SET_ROLE_SUCCEEDED':
        case 'ORGANIZATION_PROJECT_GROUP_SET_ROLE_SUCCEEDED':
        case 'ORGANIZATION_PROJECT_GROUP_DELETE_ROLE_SUCCEEDED': {
            const { organization } = (action.data ?? {}) as { organization?: IOrganizationJSON };
            if (!organization) {
                return {
                    ...state,
                    isLoading: false
                };
            }
            let computedState = {};
            const { users, teams, id: organizationId } = organization;
            const { teamById, userById } = deriveUserTeamRelations(users ?? [], teams ?? []);
            const { roleByUserOrTeamId } = deriveRolesAssigned(organization);
            computedState = { teamById, userById, roleByUserOrTeamId };

            return {
                ...state,
                ...organization,
                organizationId,
                ...computedState,
                users: state.users,
                isLoading: false,
                permissions: organization.permissions as OrganizationPermission[]
            };
        }
        case 'ORGANIZATION_PROJECT_GROUP_UPDATE_COUNTS_SUCCEEDED': {
            const { projectGroupCounts } = (action.data ?? {}) as { projectGroupCounts?: { [key: string]: number } };
            return {
                ...state,
                isLoading: false,
                projectGroupCounts: projectGroupCounts ?? {}
            };
        }
        case 'ORGANIZATION_FETCH_FAILED':
        case 'ORGANIZATION_REMOVE_USER_FAILED':
        case 'ORGANIZATION_CREATE_TEAM_FAILED':
        case 'ORGANIZATION_EDIT_TEAM_FAILED':
        case 'ORGANIZATION_DELETE_TEAM_FAILED':
        case 'ORGANIZATION_CREATE_PROJECT_GROUP_FAILED':
        case 'ORGANIZATION_EDIT_PROJECT_GROUP_FAILED':
        case 'ORGANIZATION_DELETE_PROJECT_GROUP_FAILED':
        case 'ORGANIZATION_TEAM_ADD_USERS_FAILED':
        case 'ORGANIZATION_TEAM_REMOVE_USER_FAILED':
        case 'ORGANIZATION_PROJECT_GROUP_SET_ROLE_FAILED':
        case 'ORGANIZATION_PROJECT_GROUP_DELETE_ROLE_FAILED':
        case 'ORGANIZATION_PROJECT_GROUP_UPDATE_COUNTS_FAILED':
            return {
                ...state,
                isLoading: false
            };

        case HYDRATE_STATE: {
            return {
                ...getDefaultState(),
                ...((action.data as Partial<RootState>).org?.organization ?? {})
            };
        }

        case LOGOUT_ACTION: {
            return {
                ...state,
                organizationId: undefined
            };
        }

        default: {
            return state;
        }
    }
};

export const localStorePaths = ['organization.organizationId'];

export default (state = getDefaultState(), action: $TSFixMe): State => {
    const orgState = organizationReducer(state, action);
    return {
        ...orgState,
        users: organizationUsersReducer(orgState.users, action)
    };
};
